import React from "react";
import { CourseTableRowPropsT } from "@interfaces/internal-study/courses";
import { getCourseThumbnail } from "@utils/getCompanyLogo";
import clsx from "clsx";
import style from "@scss/pages/study-center.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import { ArrowDropDownGraySVG } from "@components/svgs/arrow-drop-down-gray";
import { AttentionSVG } from "@components/svgs";
import { NotePencilIcon, TrashIcon } from "@phosphor-icons/react";
import SyncIcon from "@mui/icons-material/Sync";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  Stack,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useBoolean } from "@hooks/useBoolean";
import { useModal } from "@modals/index";
import InternalStudyСoursesTableRowLessons from "./courses_row_lessons";

const InternalStudyСoursesTableRow: React.FC<CourseTableRowPropsT> = (
  props
) => {
  const { course, onSyncCourse, onSyncLesson, onRemoveCourse, onRemoveLesson } =
    props;
  const collapse = useBoolean();
  const { open } = useModal();

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            color={collapse.value ? "inherit" : "default"}
            onClick={collapse.onToggle}
            sx={{
              ...(collapse.value && {
                bgcolor: "action.hover",
              }),
              padding: 2,
            }}
            className={clsx(style.courses_table_collapseIcon)}
          >
            <WrapperSVG color={"gray"}>
              <ArrowDropDownGraySVG
                className={clsx({
                  [style.courses_table_collapseIcon_rotated]: collapse.value,
                })}
              />
            </WrapperSVG>
          </IconButton>
        </TableCell>
        {props.thumbnail && (
          <TableCell>
            {course.thumbnail ? (
              <img
                src={getCourseThumbnail(course.thumbnail)}
                alt=""
                className={style.courseThumbnail}
              />
            ) : (
              "Не назначено"
            )}
          </TableCell>
        )}
        <TableCell>{course.title}</TableCell>
        <TableCell>{course.childs?.length ?? course.lessons_count}</TableCell>
        <TableCell sx={{ px: 1, whiteSpace: "nowrap" }}>
          <Tooltip title="Изменить" placement="top">
            <button
              className={clsx(style.event_item__button)}
              onClick={(e) => {
                e.stopPropagation();
                open("EditInternalCourseModal", {
                  btnText: "Сохранить",
                  modalData: {
                    id: course.id,
                    title: course.title,
                    programId: course.programId,
                    sync: course.sync,
                  },
                });
              }}
            >
              <WrapperSVG color="orange" margin>
                <NotePencilIcon size={24} />
              </WrapperSVG>
            </button>
          </Tooltip>
          <Tooltip title="Синхронизировать" placement="top">
            <button
              className={clsx(style.event_item__button)}
              onClick={(e) => {
                e.stopPropagation();
                if (onSyncCourse) onSyncCourse(course.uuidSDO);
              }}
            >
              <WrapperSVG color="green-light" margin>
                <SyncIcon sx={{ display: "block" }} />
              </WrapperSVG>
            </button>
          </Tooltip>
          <Tooltip title="Удалить" placement="top">
            <button
              className={clsx(style.event_item__button)}
              onClick={(e) => {
                if (onRemoveCourse) onRemoveCourse(course);
              }}
            >
              <WrapperSVG color="" margin>
                <TrashIcon size={24} />
              </WrapperSVG>
            </button>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: "none" }} colSpan={5}>
          <Collapse
            in={collapse.value}
            timeout="auto"
            unmountOnExit
            sx={{ bgcolor: "background.neutral" }}
          >
            <Stack
              component={Paper}
              className={clsx(style.courses_depth)}
              sx={{
                m: 0.5,
                whiteSpace: "normal",
                wordWrap: "break-word",
                boxShadow:
                  "0px 2px 1px -1px #bcf5dd, 0px 1px 1px 0px #bcf5dd, 0px 1px 3px 0px #bcf5dd",
              }}
              direction="row"
              justifyContent="space-between"
            >
              {course.childs && course.childs?.length > 0 && (
                <TableContainer
                  className={clsx(
                    tableStyle.base_table_container,
                    style.courses_table
                  )}
                  sx={{ position: "relative", width: "100%" }}
                >
                  <Table className={clsx(tableStyle.base_table)}>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        {course.childs!.filter((e) => e.thumbnail !== null)
                          .length > 0 && <TableCell>Обложка</TableCell>}
                        <TableCell>Название курса</TableCell>
                        <TableCell>Кол-во вложенных лекций</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {course.childs &&
                        course.childs?.length > 0 &&
                        course.childs?.map((cours) => (
                          <InternalStudyСoursesTableRow
                            course={cours}
                            thumbnail={
                              course.childs!.filter((e) => e.thumbnail !== null)
                                .length > 0
                            }
                            onSyncCourse={onSyncCourse}
                            onSyncLesson={onSyncLesson}
                            onRemoveCourse={onRemoveCourse}
                            onRemoveLesson={onRemoveLesson}
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {course.lessons && course.lessons?.length > 0 && (
                <TableContainer
                  className={clsx(
                    tableStyle.base_table_container,
                    style.courses_table,
                    style.courses_lessons
                  )}
                  sx={{ position: "relative", width: "100%" }}
                >
                  <Table className={clsx(tableStyle.base_table)}>
                    <TableHead>
                      <TableRow>
                        {course.lessons!.filter((e) => e.thumbnail !== null)
                          .length > 0 && <TableCell>Обложка</TableCell>}
                        <TableCell>Название урока</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          Дней до отключения
                          <Tooltip
                            title={"Урок будет недоступен после прохождения"}
                            placement="top"
                          >
                            <div
                              className="attention"
                              style={{
                                display: "inline-block",
                                marginLeft: "5px",
                                verticalAlign: "middle",
                              }}
                            >
                              <AttentionSVG />
                            </div>
                          </Tooltip>
                        </TableCell>
                        <TableCell>Кол-во попыток в день</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {course.lessons &&
                        course.lessons?.length > 0 &&
                        course.lessons?.map((lesson) => (
                          <InternalStudyСoursesTableRowLessons
                            lesson={lesson}
                            thumbnail={
                              course.lessons!.filter(
                                (e) => e.thumbnail !== null
                              ).length > 0
                            }
                            onSyncLesson={onSyncLesson}
                            onRemoveLesson={onRemoveLesson}
                            course={course.uuidSDO}
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default InternalStudyСoursesTableRow;
